import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { parseServerDate } from '../i18n/format';

interface DlpActivityProps {
  apiUrl: string;
}

interface DlpEvent {
  id?: number;
  timestamp: string;
  service: string;
  tool_name: string;
  status: string;
  result: string;
  agent_id: string;
  org_id: string;
  arguments?: string;
  device_hostname?: string | null;
  display_name?: string | null;
}

const DlpActivity: React.FC<DlpActivityProps> = ({ apiUrl }) => {
  const { t } = useTranslation();
  const [events, setEvents] = useState<DlpEvent[]>([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState<'all' | 'agent' | 'browser'>('all');
  const [autoRefresh, setAutoRefresh] = useState(true);

  const fetchEvents = useCallback(async () => {
    try {
      const data = await fetch(`${apiUrl}/admin/bdr/dlp-events`).then((r) => r.json());
      setEvents(Array.isArray(data.events) ? data.events : []);
    } catch (err) {
      console.error('Failed to fetch DLP events:', err);
    } finally {
      setLoading(false);
    }
  }, [apiUrl]);

  useEffect(() => {
    fetchEvents();
    if (!autoRefresh) return;
    const interval = setInterval(fetchEvents, 10000);
    return () => clearInterval(interval);
  }, [autoRefresh, fetchEvents]);

  const kindOf = (service: string): 'agent' | 'browser' | 'other' => {
    if (service === 'bdr-agent-dlp') return 'agent';
    if (service === 'browser-dlp') return 'browser';
    return 'other';
  };

  const badgeFor = (kind: string) =>
    kind === 'agent' ? 'badge-info' : kind === 'browser' ? 'badge-redact' : 'badge-unknown';

  const parseArgs = (args?: string): any => {
    try {
      return args ? JSON.parse(args) : {};
    } catch {
      return {};
    }
  };

  const filtered = events.filter((e) => {
    if (filter === 'agent') return e.service === 'bdr-agent-dlp';
    if (filter === 'browser') return e.service === 'browser-dlp';
    return true;
  });

  const agentCount = events.filter((e) => e.service === 'bdr-agent-dlp').length;
  const browserCount = events.filter((e) => e.service === 'browser-dlp').length;

  return (
    <div className="page-content">
      <div className="page-title">
        <div>
          <h1>{t('pages.dlpActivity.dlpActivity')}</h1>
          <p className="subtitle">{t('pages.dlpActivity.dataLossViolationsFromClaudeDesktop')}</p>
        </div>
        <div className="ent-toolbar" style={{ margin: 0 }}>
          <label style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
            <input type="checkbox" checked={autoRefresh} onChange={(e) => setAutoRefresh(e.target.checked)} />
            {t('pages.dlpActivity.autoRefresh')}
          </label>
          <button className="ent-btn" onClick={fetchEvents} disabled={loading}>
            {t('pages.dlpActivity.refresh')}
          </button>
        </div>
      </div>

      <div className="metric-row">
        <div className="metric accent-danger">
          <span className="label">{t('pages.dlpActivity.totalViolations')}</span>
          <div className="value">{events.length}</div>
        </div>
        <div className="metric accent-info">
          <span className="label">{t('pages.dlpActivity.fromEndpointAgents')}</span>
          <div className="value">{agentCount}</div>
        </div>
        <div className="metric accent-warning">
          <span className="label">{t('pages.dlpActivity.fromBrowser')}</span>
          <div className="value">{browserCount}</div>
        </div>
      </div>

      <div className="ent-toolbar">
        {(['all', 'agent', 'browser'] as const).map((f) => (
          <button
            key={f}
            className={`ent-btn ${filter === f ? 'primary' : ''}`}
            onClick={() => setFilter(f)}
          >
            {f === 'all' ? t('pages.dlpActivity.allEvents') : f === 'agent' ? t('pages.dlpActivity.agentDLP') : t('pages.dlpActivity.browserDLP')}
          </button>
        ))}
      </div>

      <div className="ent-table-wrap scroll">
        <table className="ent-table">
          <thead>
            <tr>
              <th style={{ width: 180 }}>{t('pages.dlpActivity.time')}</th>
              <th style={{ width: 110 }}>{t('pages.dlpActivity.source')}</th>
              <th style={{ width: 180 }}>{t('pages.dlpActivity.destinationApp')}</th>
              <th>{t('pages.dlpActivity.detail')}</th>
              <th style={{ width: 180 }}>{t('pages.dlpActivity.endpoint')}</th>
            </tr>
          </thead>
          <tbody>
            {loading && events.length === 0 ? (
              <tr>
                <td colSpan={5} className="ent-empty">{t('pages.dlpActivity.loading')}</td>
              </tr>
            ) : filtered.length === 0 ? (
              <tr>
                <td colSpan={5} className="ent-empty">{t('pages.dlpActivity.noDLPViolationsDetected')}</td>
              </tr>
            ) : (
              filtered.map((e, idx) => {
                const kind = kindOf(e.service);
                const args = parseArgs(e.arguments);
                return (
                  <tr key={e.id ?? idx}>
                    <td>{parseServerDate(e.timestamp).toLocaleString()}</td>
                    <td>
                      <span className={`badge ${badgeFor(kind)}`}>
                        {kind === 'agent' ? t('pages.dlpActivity.agent') : kind === 'browser' ? t('pages.dlpActivity.browser') : t('pages.dlpActivity.dlp')}
                      </span>
                    </td>
                    <td>{e.tool_name || '—'}</td>
                    <td>{args.detail || e.result || '—'}</td>
                    <td title={args.device || e.agent_id || ''}>
                      {e.device_hostname || e.display_name || args.device || e.agent_id || '—'}
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default DlpActivity;
